import React, { useEffect, useEffectEvent, useRef } from 'react'
import VimeoPlayer from '@vimeo/player'

// Accepts a numeric id, or "id/hash" for unlisted videos.
const videoOptions = videoId => {
  const [id, hash] = String(videoId).split('/')
  return hash ? { url: `https://vimeo.com/${id}/${hash}` } : { id: Number(id) }
}

// Vimeo player built with @vimeo/player. Reports play, pause and end to the
// slideshow and is destroyed on unmount.
const Vimeo = ({ videoId, autoplay = false, title, onPlay, onPause, onEnded }) => {
  const containerRef = useRef(null)

  const handlePlay = useEffectEvent(() => onPlay?.())
  const handlePause = useEffectEvent(() => onPause?.())
  const handleEnded = useEffectEvent(() => onEnded?.())

  useEffect(() => {
    const container = containerRef.current
    // The player appends its iframe asynchronously, so give it an element
    // React doesn't own.
    const target = document.createElement('div')
    container.appendChild(target)

    let destroyed = false

    const player = new VimeoPlayer(target, {
      ...videoOptions(videoId),
      autoplay,
      muted: autoplay,
      playsinline: true
    })
    player.on('play', () => {
      if (!destroyed) handlePlay()
    })
    player.on('pause', () => {
      if (!destroyed) handlePause()
    })
    player.on('ended', () => {
      if (!destroyed) handleEnded()
    })
    // A load or playback error stops the video, so hand control back to the timer.
    player.on('error', () => {
      if (!destroyed) handlePause()
    })
    player
      .ready()
      .then(() => {
        const iframe = target.querySelector('iframe')
        if (!destroyed && iframe && title) iframe.title = title
      })
      .catch(() => {
        if (!destroyed) handlePause()
      })

    return () => {
      destroyed = true
      player.destroy().catch(() => {})
      container.replaceChildren()
    }
  }, [videoId, autoplay, title])

  return (
    <div
      ref={containerRef}
      className='absolute inset-0 [&_div]:w-full [&_div]:h-full [&_iframe]:w-full [&_iframe]:h-full'
    />
  )
}

export default Vimeo
